import { useEffect, useState } from 'react';

import { useGetRisks } from '@/hooks/useRisks';
import { RiskStatus } from '@/types';
import { getRiskStatusLabel } from '@/utils/riskStatus.util';

/**
 * Reusable risk-selection step shared by the Generate Report and Generate LOA
 * wizards. Lets the user choose which risks an export covers and resolves that
 * choice to a `risk_keys` array.
 *
 * Resolution semantics (`resolvedRiskKeys`):
 *  - `'all'`       → `undefined` (export covers ALL open risks; no keys sent).
 *  - `'specific'`  → the explicitly picked keys, or `undefined` when none picked.
 *  - `'dateRange'` → the keys of open risks whose chosen date field falls inside
 *                    `[startDate, endDate]`, resolved live via `useGetRisks`.
 *
 * The hook instance is owned by the parent wizard (not the step component) so the
 * resolved keys are available at submit time.
 *
 * Pagination — IMPORTANT: `useGetRisks` is backed by `useInfiniteQuery` with a
 * 100-rows-per-page cap. Reading `query.data` alone would yield only the FIRST
 * page, silently capping the resolved scope (and the "specific" picker) at 100
 * risks — so a date-range/LOA export would omit every matching finding beyond
 * the first 100, and the picker could never reach the rest. To avoid that, this
 * hook auto-fetches every page to exhaustion (`fetchNextPage` while
 * `hasNextPage`) before exposing keys/options, and reports `isResolving` /
 * `isLoadingOptions` while pages are still loading so the wizards disable
 * Next/Generate until the COMPLETE set is loaded.
 */

export type RiskSelectionMode = 'all' | 'dateRange' | 'specific';

/** Which Risk date field the date-range filter compares against. */
export type RiskDateField = 'created' | 'visited';

/** A pickable open risk for the "specific" multi-select. */
export interface RiskOption {
  key: string;
  label: string;
  /** Full status code (e.g. `OC`), for StatusChip variant + label lookup. */
  status: string;
}

export interface RiskSelectionState {
  mode: RiskSelectionMode;
  setMode: (mode: RiskSelectionMode) => void;

  dateField: RiskDateField;
  setDateField: (field: RiskDateField) => void;
  startDate: string;
  setStartDate: (date: string) => void;
  endDate: string;
  setEndDate: (date: string) => void;

  selectedKeys: string[];
  setSelectedKeys: (keys: string[]) => void;

  /**
   * The resolved `risk_keys` payload. `undefined` means "all open risks"
   * (mode `'all'`, an empty `'specific'` selection, or a `'dateRange'` whose
   * bounds are not yet both set).
   */
  resolvedRiskKeys: string[] | undefined;
  /**
   * True while the date-range query is fetching its matched keys (including
   * paging the full result set to exhaustion). The wizards gate Next/Generate
   * on this so the export never submits a capped/partial key set.
   */
  isResolving: boolean;
  /** Open risks available to pick in `'specific'` mode. */
  riskOptions: RiskOption[];
  /**
   * True while the `'specific'` picker is still loading its open-risk options
   * (including paging to exhaustion). Lets the picker distinguish "still
   * loading" from a genuinely empty result, so the empty state never lies.
   */
  isLoadingOptions: boolean;
}

// Open risks only — the export wizards cover what's currently open. Mirrors the
// 'O' status code (RiskStatus.Opened) the risks table uses for "Open".
const OPEN_STATUS_FILTER = [RiskStatus.Opened];

export function useRiskSelection(): RiskSelectionState {
  const [mode, setMode] = useState<RiskSelectionMode>('all');
  const [dateField, setDateField] = useState<RiskDateField>('created');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  const isDateRangeMode = mode === 'dateRange';
  const isSpecificMode = mode === 'specific';
  const hasDateRange = !!startDate && !!endDate;

  // Date-range resolution. `useGetRisks` only honors `advancedFilters` (including
  // `dateRange`) when it runs in "drawer context" — i.e. `parentFilters` is set —
  // so the filter is carried via `parentFilters`, not `initialFilters` (which is
  // only the seed for the inert ephemeral-filter state). See useRisks.ts.
  const dateRangeQuery = useGetRisks({
    enabled: isDateRangeMode && hasDateRange,
    parentFilters: {
      advancedFilters: {
        status: OPEN_STATUS_FILTER,
        dateRange: { field: dateField, startDate, endDate },
      },
    },
  });

  // Open-risk list for the "specific" multi-select picker.
  const specificQuery = useGetRisks({
    enabled: isSpecificMode,
    parentFilters: {
      advancedFilters: {
        status: OPEN_STATUS_FILTER,
      },
    },
  });

  // Drive both infinite queries to exhaustion. `useGetRisks` pages 100 rows at a
  // time; without this, `.data` would only ever hold the first page. We fetch
  // the next page whenever one exists and we aren't already fetching — the
  // effect re-runs as each page lands (hasNextPage / isFetching change) until
  // `hasNextPage` is false, at which point `.data` holds the COMPLETE set.
  // Guarded on `hasNextPage` so there is no infinite loop.
  useEffect(() => {
    if (
      isDateRangeMode &&
      hasDateRange &&
      dateRangeQuery.hasNextPage &&
      !dateRangeQuery.isFetching
    ) {
      dateRangeQuery.fetchNextPage();
    }
  }, [
    isDateRangeMode,
    hasDateRange,
    dateRangeQuery.hasNextPage,
    dateRangeQuery.isFetching,
    dateRangeQuery.fetchNextPage,
  ]);

  useEffect(() => {
    if (
      isSpecificMode &&
      specificQuery.hasNextPage &&
      !specificQuery.isFetching
    ) {
      specificQuery.fetchNextPage();
    }
  }, [
    isSpecificMode,
    specificQuery.hasNextPage,
    specificQuery.isFetching,
    specificQuery.fetchNextPage,
  ]);

  const riskOptions: RiskOption[] = specificQuery.data.map(risk => ({
    key: risk.key,
    label: risk.title || risk.name,
    status: risk.status,
  }));

  const dateRangeKeys = dateRangeQuery.data.map(risk => risk.key);

  let resolvedRiskKeys: string[] | undefined;
  if (mode === 'all') {
    resolvedRiskKeys = undefined;
  } else if (mode === 'specific') {
    resolvedRiskKeys = selectedKeys.length > 0 ? selectedKeys : undefined;
  } else {
    // 'dateRange': resolved keys only once both bounds are set; otherwise treat
    // as unconstrained (undefined = all open risks). `dateRangeKeys` reflects
    // the COMPLETE paged result (see the exhaustion effect above).
    resolvedRiskKeys = hasDateRange ? dateRangeKeys : undefined;
  }

  // Resolving while the initial page is pending OR while more pages remain to be
  // fetched (hasNextPage). Either state means `dateRangeKeys` is not yet the
  // full set, so the wizards must keep Next/Generate disabled.
  const isResolving =
    isDateRangeMode &&
    hasDateRange &&
    (dateRangeQuery.status === 'pending' || !!dateRangeQuery.hasNextPage);

  // Same exhaustion gate for the "specific" picker so its empty state never
  // shows "no risks" while pages are still loading.
  const isLoadingOptions =
    isSpecificMode &&
    (specificQuery.status === 'pending' || !!specificQuery.hasNextPage);

  return {
    mode,
    setMode,
    dateField,
    setDateField,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    selectedKeys,
    setSelectedKeys,
    resolvedRiskKeys,
    isResolving,
    riskOptions,
    isLoadingOptions,
  };
}

// Re-exported for callers that want the label for a risk status code.
export { getRiskStatusLabel };
